import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { mergeGuestXpToAccount, publishXp, XP_EVENT } from './xp';
import { useCurrentXp } from './useXpTicker';
import { GoogleMark } from './GoogleSignInButton';

// Client-side Auth component for React
const Auth = ({ onLogin, onLogout }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginAttemptFailed, setLoginAttemptFailed] = useState(false);
  const [myHallPath, setMyHallPath] = useState(null);
  const statusCheckRef = useRef(null);
  const initialCheckDone = useRef(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isAppMode = location.pathname.startsWith('/app');
  const { xp: xpDisplay } = useCurrentXp({
    isAuthenticated,
    seedXp: user?.xp
  });

  useEffect(() => {
    if (!isAuthenticated || !user?.userId) {
      setMyHallPath(null);
      return undefined;
    }
    let cancelled = false;
    const preferredSlug = user.username ? `@${String(user.username).toLowerCase()}` : '';
    axios.get('/api/halls/mine', { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const halls = Array.isArray(res.data?.halls) ? res.data.halls : [];
        const preferred = halls.find((h) => String(h.slug || '').toLowerCase() === preferredSlug)
          || halls[0];
        const slug = preferred?.slug ? String(preferred.slug) : '';
        setMyHallPath(slug ? `/${slug}` : null);
      })
      .catch(() => {
        if (!cancelled) setMyHallPath(null);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.userId, user?.username]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const onXp = (e) => {
      if (typeof e.detail?.xp !== 'number') return;
      setUser((prev) => (prev ? { ...prev, xp: e.detail.xp } : prev));
    };
    window.addEventListener(XP_EVENT, onXp);
    return () => window.removeEventListener(XP_EVENT, onXp);
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (loginAttemptFailed) return;

      try {
        const res = await axios.get('/api/auth/status', {
          withCredentials: true,
          params: { t: Date.now() }
        });

        if (res.data.isAuthenticated) {
          // Check if the user is a new user who hasn't completed account creation
          if (res.data.user.newUser) {
            console.log('User has not completed account creation');
            setIsAuthenticated(false);
            setUser(null);
            return;
          }

          if (res.data.user.pendingGoogleLink) {
            setIsAuthenticated(false);
            setUser(null);
            if (window.location.pathname !== '/connect-google') {
              window.location.href = '/connect-google';
            }
            return;
          }

          if (res.data.user.needsGoogleLink || (res.data.user.steamId64 && !res.data.user.googleId)) {
            if (window.location.pathname !== '/connect-google') {
              window.location.href = '/connect-google';
            }
            return;
          }

          // Check if user is banned
          if (res.data.user.status === 'Banned') {
            setAuthError('Your account has been banned. Please contact an administrator for assistance.');
            setLoginAttemptFailed(true);
            // Log them out
            try {
              await axios.get('/api/auth/logout', { withCredentials: true });
            } catch (logoutError) {
              console.error('Error logging out banned user:', logoutError);
            }
            setIsAuthenticated(false);
            setUser(null);
            if (onLogout) onLogout();
            return;
          }

          setIsAuthenticated(true);
          setUser(res.data.user);
          if (typeof res.data.user?.xp === 'number') {
            publishXp(res.data.user.xp);
          }
          if (onLogin) onLogin(res.data.user);
          mergeGuestXpToAccount().then((xp) => {
            if (typeof xp === 'number') {
              setUser((prev) => (prev ? { ...prev, xp } : prev));
            }
          });
          // Clear interval if authenticated
          if (statusCheckRef.current) {
            clearInterval(statusCheckRef.current);
            statusCheckRef.current = null;
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setIsAuthenticated(false);
        setUser(null);
        setLoginAttemptFailed(true);
        // Clear interval on error
        if (statusCheckRef.current) {
          clearInterval(statusCheckRef.current);
          statusCheckRef.current = null;
        }
      } finally {
        if (!initialCheckDone.current) {
          setLoading(false);
          initialCheckDone.current = true;
        }
      }
    };

    // Check URL for auth errors
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    const youtubeError = params.get('youtube_error');
    const message = params.get('message');
    const stripAuthParams = () => {
      const path = window.location.pathname;
      window.history.replaceState({}, document.title, path);
    };

    if (youtubeError) {
      console.error('YouTube connect error:', youtubeError, message);
      setAuthError(message || 'Could not connect YouTube');
      setLoading(false);
      stripAuthParams();
      checkAuthStatus();
    } else if (error) {
      console.error('Auth error from URL:', error, message);
      setAuthError(message || 'Authentication failed');
      setLoginAttemptFailed(true);
      setLoading(false);
      stripAuthParams();
    } else if (!isAuthenticated && !loginAttemptFailed && !statusCheckRef.current) {
      checkAuthStatus();
      statusCheckRef.current = setInterval(checkAuthStatus, 5000);
    }

    // Cleanup
    return () => {
      if (statusCheckRef.current) {
        clearInterval(statusCheckRef.current);
        statusCheckRef.current = null;
      }
    };
  }, [onLogin, onLogout, isAuthenticated, loginAttemptFailed]);

  // Handle Google login
  const handleGoogleLogin = () => {
    setAuthError(null);
    setLoginAttemptFailed(false);
    setLoading(true);
    if (statusCheckRef.current) {
      clearInterval(statusCheckRef.current);
      statusCheckRef.current = null;
    }
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = '/api/auth/google?returnTo=' + encodeURIComponent(currentPath);
  };

  // Handle retry
  const handleRetry = () => {
    setAuthError(null);
    setLoginAttemptFailed(false);
    setLoading(false);
    initialCheckDone.current = false;
    // Clear session cookie
    document.cookie = 'connect.sid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      window.dispatchEvent(new Event('bigplays:logout'));
      await axios.get('/api/auth/logout', { withCredentials: true });
      setIsAuthenticated(false);
      setUser(null);
      setLoginAttemptFailed(false);
      initialCheckDone.current = false;
      if (onLogout) onLogout();
      // Clear session cookie
      document.cookie = 'connect.sid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    } catch (error) {
      console.error('Error logging out:', error);
      setAuthError('Failed to logout. Please try again.');
    }
  };

  // Toggle dropdown menu
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Get status display style - exported for use in other components
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Admin':
        return 'text-blue-800 border-blue-800';
      case 'Owner':
        return 'text-purple-800 border-purple-800';
      case 'Moderator':
        return 'text-green-800 border-green-800';
      default:
        return 'text-gray-800 border-gray-800';
    }
  };

  // Get status underline style
  const getStatusUnderlineStyle = (status) => {
    switch (status) {
      case 'Admin':
        return 'border-b-2 border-blue-500';
      case 'Owner':
        return 'border-b-2 border-purple-500';
      case 'Moderator':
        return 'border-b-2 border-green-500';
      default:
        return '';
    }
  };

  const fallbackAvatar = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';
  const menuItemClass = 'block w-full text-left px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100';
  const menuRowItemClass = 'block flex-1 text-left px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100';

  return (
    <div className="auth-container relative">
      {authError && (
        <div className="absolute top-10 sm:top-12 right-0 bg-red-100 border border-red-400 text-red-700 px-2 py-1 sm:px-4 sm:py-2 rounded mb-2 text-xs sm:text-sm w-64 sm:w-80 z-50">
          <button className="float-right text-red-700" onClick={() => setAuthError(null)}>×</button>
          {authError}
          {loginAttemptFailed && (
            <button
              onClick={handleRetry}
              className="block w-full mt-1 sm:mt-2 text-center bg-red-200 hover:bg-red-300 rounded py-1 text-xs sm:text-sm"
            >
              Retry Login
            </button>
          )}
        </div>
      )}

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={toggleDropdown}
          className="focus:outline-none flex items-center"
          aria-label={isAuthenticated ? 'Account menu' : 'Sign in and legal menu'}
          aria-expanded={showDropdown}
        >
          {loading && !loginAttemptFailed && !isAuthenticated ? (
            <span className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border-2 border-white bg-gray-700 animate-pulse" />
          ) : (
            <img
              src={user?.avatar || fallbackAvatar}
              alt={isAuthenticated ? 'User Avatar' : 'Account'}
              className="w-8 h-8 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border-2 border-white cursor-pointer"
              onError={(e) => {
                e.target.src = fallbackAvatar;
              }}
            />
          )}
        </button>

        {showDropdown && (
          <div className={`absolute right-0 mt-1 sm:mt-2 ${myHallPath ? 'w-56' : 'w-48'} bg-white rounded-md shadow-lg py-1 z-50`}>
            {isAuthenticated ? (
              <>
                <div className="px-3 py-1 sm:px-4 sm:py-2 border-b border-gray-100">
                  <div className={`text-sm font-medium text-gray-900 ${user?.status ? getStatusUnderlineStyle(user.status) : ''}`}>
                    {user?.username}
                  </div>
                  <div className="text-xs text-yellow-600 font-semibold">
                    {xpDisplay} XP
                  </div>
                </div>
                <div className="flex items-stretch">
                  <Link
                    to={`${isAppMode ? '/app/user' : '/user'}/${user?.username}`}
                    className={myHallPath ? menuRowItemClass : menuItemClass}
                    onClick={() => setShowDropdown(false)}
                  >
                    Profile
                  </Link>
                  {myHallPath && (
                    <Link
                      to={myHallPath}
                      className={`${menuRowItemClass} border-l border-gray-100`}
                      onClick={() => setShowDropdown(false)}
                    >
                      My Hall
                    </Link>
                  )}
                </div>
                <Link
                  to="/privacy"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Terms
                </Link>
                <Link
                  to="/report"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Report
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={menuItemClass}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setShowDropdown(false);
                    handleGoogleLogin();
                  }}
                  disabled={loading}
                  className={`${menuItemClass} flex items-center gap-2 disabled:opacity-50`}
                >
                  <GoogleMark className="w-4 h-4" />
                  Log in with Google
                </button>
                <Link
                  to="/privacy"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Terms
                </Link>
                <Link
                  to="/report"
                  className={menuItemClass}
                  onClick={() => setShowDropdown(false)}
                >
                  Report
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;